// ============================================
// useCalendlyMessageListener — Calendly iframe postMessage handler
// ============================================
// Extracted from VoiceBotPanel.tsx in Wave 6d of the widget refactor.
//
// Listens for `message` events from the Calendly embed iframe and:
//   1. Captures the user's selected slot (`calendly.date_and_time_selected`).
//   2. On `calendly.event_scheduled`:
//      - Fetches the actual booking time via our backend, updates
//        `scheduledMeeting`/`scheduledMeetingRef`.
//      - Flips `bookingConfirmed`, hides the Calendly embed.
//      - Sends a language-aware goodbye instruction to the realtime
//        data channel (once, gated by `goodbyeSentRef`).
//      - Schedules a forced `endSession()` after
//        `TIMING.CALENDLY_FORCE_CLOSE_MS`.
//
// Behavior preserved exactly. The original useEffect had `[]` deps and
// captured component-scope identifiers via closure. We mirror that with
// a ref that holds the latest options, so the listener never re-attaches
// across renders but always reads the current setters/refs/callbacks.

import { MutableRefObject, useEffect, useRef } from 'react'
import { CALENDLY_SYSTEM_MESSAGES, LanguageCode } from '../config/language'
import { TIMING } from '../constants/voice-bot-timing'
import { fetchCalendlyEvent } from '../api/voice-bot-client'
import type { ScheduledMeeting } from '../types'

export interface CalendlyMessageListenerOptions {
  // Refs read live inside the listener
  calendlySelectedTimeRef: MutableRefObject<string | null>
  scheduledMeetingRef: MutableRefObject<ScheduledMeeting | null>
  sessionDataRef: MutableRefObject<{ customerId?: string } | null>
  calendlyEventFetchRef: MutableRefObject<Promise<unknown> | null>
  selectedLanguageRef: MutableRefObject<LanguageCode>
  dataChannelRef: MutableRefObject<RTCDataChannel | null>
  goodbyeSentRef: MutableRefObject<boolean>
  bookingConfirmedRef: MutableRefObject<boolean>
  // Setters for component state
  setScheduledMeeting: (m: ScheduledMeeting) => void
  setBookingConfirmed: (v: boolean) => void
  setShowCalendlyEmbed: (v: boolean) => void
  // Callbacks owned by the component (defined inline / via useCallback)
  formatTime: (dateStr: string, lang: LanguageCode) => string
  triggerResponse: (reason: string) => void
  endSession: () => void
}

export const useCalendlyMessageListener = (
  options: CalendlyMessageListenerOptions
): void => {
  const optionsRef: MutableRefObject<CalendlyMessageListenerOptions> = useRef(options)
  optionsRef.current = options

  useEffect(() => {
    const handleCalendlyMessage = (event: MessageEvent) => {
      const o = optionsRef.current

      const isCalendlyOrigin =
        event.origin === 'https://calendly.com' ||
        event.origin.endsWith('.calendly.com')

      if (!isCalendlyOrigin) {
        return
      }

      // Log ALL messages to debug Calendly events
      if (event.data?.event && event.data.event.startsWith('calendly.')) {
        console.log('📨 Calendly PostMessage:', event.data)
      }

      // Capture the selected time when user picks a slot (BEFORE booking is confirmed)
      if (event.data?.event === 'calendly.date_and_time_selected') {
        const selectedPayload = event.data?.payload
        console.log('🕐 Calendly date_and_time_selected payload:', JSON.stringify(selectedPayload, null, 2))

        // Extract start_time from the selection event
        const selectedTime =
          selectedPayload?.event?.start_time ||
          selectedPayload?.start_time ||
          selectedPayload?.slot?.start_time ||
          null

        if (selectedTime) {
          console.log('✅ Captured selected time from Calendly:', selectedTime)
          o.calendlySelectedTimeRef.current = selectedTime
        } else {
          console.log('⚠️ date_and_time_selected payload has no start_time:', selectedPayload)
        }
      }

      // Calendly sends 'calendly.event_scheduled' when user completes booking
      if (event.data?.event === 'calendly.event_scheduled') {
        console.log('✅ CALENDLY BOOKING CONFIRMED! Starting goodbye sequence...')
        console.log('📦 Full Calendly payload:', JSON.stringify(event.data, null, 2))

        // Calendly only sends URIs in postMessage - we need to call our API to get actual booking time
        const eventPayload = event.data?.payload
        const eventUri = eventPayload?.event?.uri

        if (eventUri && o.scheduledMeetingRef.current) {
          console.log('📅 Fetching actual booking time from Calendly API via:', eventUri)

          // Fetch actual event details from our API
          // Store the promise so endSession() can await it before sending to n8n
          const customerId = o.sessionDataRef.current?.customerId
          o.calendlyEventFetchRef.current = fetchCalendlyEvent(eventUri, customerId)
            .then((res: Response) => res.json())
            .then((data: { success?: boolean; event?: { start_time?: string } }) => {
              if (data.success && data.event?.start_time) {
                const formattedTime = o.formatTime(data.event.start_time, o.selectedLanguageRef.current)
                console.log('📅 Got actual booking time from API:', {
                  original: o.scheduledMeetingRef.current?.formattedTime,
                  actual: formattedTime,
                  start_time: data.event.start_time
                })

                // Update the scheduledMeeting with the ACTUAL time user selected in Calendly
                const updatedMeeting = {
                  ...o.scheduledMeetingRef.current!,
                  datetime: data.event.start_time,
                  formattedTime: formattedTime
                }
                o.setScheduledMeeting(updatedMeeting)
                o.scheduledMeetingRef.current = updatedMeeting
              } else {
                console.warn('⚠️ API did not return start_time:', data)
              }
            })
            .catch((err: unknown) => {
              console.error('❌ Failed to fetch event details from API:', err)
            })
        } else {
          console.warn('⚠️ No event URI in payload or no scheduled meeting:', { eventUri, hasScheduledMeeting: !!o.scheduledMeetingRef.current })
        }

        // Clear the captured time for next booking
        o.calendlySelectedTimeRef.current = null

        // Wave 6e5: setBookingConfirmed (useStateAndRef) writes the ref
        // synchronously, so no separate imperative ref write is needed.
        o.setBookingConfirmed(true)
        o.setShowCalendlyEmbed(false)

        // Send goodbye in user's selected language
        if (o.dataChannelRef.current?.readyState === 'open' && !o.goodbyeSentRef.current) {
          o.goodbyeSentRef.current = true // Prevent duplicate goodbye
          const lang = o.selectedLanguageRef.current
          const calendlyMessages = CALENDLY_SYSTEM_MESSAGES[lang] || CALENDLY_SYSTEM_MESSAGES['he']
          console.log('📢 Sending goodbye instruction to bot in', lang)
          o.dataChannelRef.current.send(JSON.stringify({
            type: 'conversation.item.create',
            item: {
              type: 'message',
              role: 'user',
              content: [{
                type: 'input_text',
                text: calendlyMessages.bookingConfirmedGoodbye
              }]
            }
          }))
          // Use centralized trigger helper
          o.triggerResponse('calendly-goodbye')
        }

        // FORCE CLOSE session (allow bot to finish speaking goodbye message)
        setTimeout(() => {
          console.log('🛑 FORCE CLOSING SESSION - Calendly booking completed!')
          o.endSession()
        }, TIMING.CALENDLY_FORCE_CLOSE_MS)
      }
    }
    window.addEventListener('message', handleCalendlyMessage)
    return () => window.removeEventListener('message', handleCalendlyMessage)
  }, [])
}
